import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import { fetchAvailableOwnerListings } from "../services/ownerPatrimonyService";
import {
  AvailableOwnerListing,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";

interface UseAvailableOwnerListingsOptions {
  accessToken: string | null;
  enabled: boolean;
}

interface UseAvailableOwnerListingsResult {
  listings: AvailableOwnerListing[];
  status: OwnerPatrimonyRequestStatus;
  retry: () => Promise<void>;
}

export function useAvailableOwnerListings({
  accessToken,
  enabled,
}: UseAvailableOwnerListingsOptions): UseAvailableOwnerListingsResult {
  const [listings, setListings] = useState<AvailableOwnerListing[]>([]);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const latestRequestIdRef = useRef(0);

  const loadListings = useCallback(async () => {
    if (!enabled || !accessToken) {
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setStatus("loading");

    try {
      api.defaults.headers.authorization = `Bearer ${accessToken}`;
      const response = await fetchAvailableOwnerListings();

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setListings(response);
      setStatus("success");
    } catch {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setListings([]);
      setStatus("error");
    }
  }, [accessToken, enabled]);

  useEffect(() => {
    if (!enabled || !accessToken) {
      setListings([]);
      setStatus("idle");
      return;
    }

    void loadListings();
  }, [accessToken, enabled, loadListings]);

  return {
    listings,
    status,
    retry: loadListings,
  };
}
